import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Tag, Row, Col, Dropdown, Modal, message, Spin } from "antd";
import {
  MoreOutlined,
  PlusOutlined,
  FileTextOutlined,
  UserOutlined,
  CheckCircleOutlined,
  EditOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import { PageHeader, MyJobsWrapper } from "./MyJobs.style";
import axios from "axios";
import { API_CONST } from "../../../const";
import { PermissionGate } from "../../../components/PermissionGate/PermissionGate";
import { usePermissions } from "../../../contexts/PermissionContext";
import BulkUploadJobs from "../../../components/BulkUploadJobs/BulkUploadJobs";

const transformJobData = (apiJob) => {
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return {
    id: apiJob.id,
    title: apiJob.role,
    type: apiJob.engagementType,
    location: apiJob.reportingLocation,
    experience: apiJob.experienceRange,
    submittedProfiles: 0,
    interviewing: 0,
    rejected: 0,
    hired: 0,
    status: apiJob.status === 'pending_approval' ? 'Pending Approval' :
            apiJob.status === 'draft' ? 'Draft' :
            apiJob.status === 'open' ? 'Active' : 'Inactive',
    rawStatus: apiJob.status,
    postedDate: formatDate(apiJob.createdAt),
  };
};

const MyJobs = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState("Active");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchedCounts, setMatchedCounts] = useState({});
  const [bulkUploadVisible, setBulkUploadVisible] = useState(false);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const response = await axios.post(API_CONST.GET_JOB_REQUIREMENTS, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const transformedJobs = (response.data.Response || []).map(transformJobData);
      
      // Fetch application counts for each job
      const jobsWithCounts = await Promise.all(
        transformedJobs.map(async (job) => {
          try {
            const appResponse = await axios.post(
              API_CONST.GET_JOB_APPLICATIONS,
              { jobId: job.id },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            const applications = appResponse.data.applications || [];
            return {
              ...job,
              submittedProfiles: applications.length,
              interviewing: applications.filter(app => app.status === 'interview_scheduled').length,
              rejected: applications.filter(app => app.status === 'rejected').length,
              hired: applications.filter(app => app.status === 'hired').length,
            };
          } catch (error) {
            console.error(`Error fetching applications for job ${job.id}:`, error);
            return job;
          }
        })
      );
      
      setJobs(jobsWithCounts);

      try {
        const jobIds = jobsWithCounts.map((job) => job.id);
        const res = await axios.post(
          API_CONST.POST_MATCHED_COUNTS,
          { jobIds },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMatchedCounts(res.data.counts || {});
      } catch (err) {
        console.error("Error fetching matched counts:", err);
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
      message.error("Failed to load jobs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, []);

  const filteredJobs = jobs.filter((job) => job.status === activeTab);

  const activeCount = jobs.filter((j) => j.status === "Active").length;
  const draftCount = jobs.filter((j) => j.status === "Draft").length;
  const pendingCount = jobs.filter((j) => j.status === "Pending Approval").length;
  const inactiveCount = jobs.filter((j) => j.status === "Inactive").length;
  const totalProfiles = jobs.reduce((sum, job) => sum + job.submittedProfiles, 0);
  const totalHired = jobs.reduce((sum, job) => sum + job.hired, 0);

  const handleEditJob = (jobId) => {
    navigate(`/customer/edit-job/${jobId}`);
  };

  const handleCloseJob = (job) => {
    Modal.confirm({
      title: "Close Job",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>Are you sure you want to close this job?</p>
          <p style={{ fontWeight: 500, marginTop: 8 }}>
            Job: {job.title} (ID: {job.id})
          </p>
          <p style={{ color: "#666", fontSize: 13 }}>
            This will move the job to Inactive status and no more profiles can be submitted.
          </p>
        </div>
      ),
      okText: "Close Job",
      okButtonProps: { 
        danger: true,
        style: { background: "#ff4d4f", borderColor: "#ff4d4f" }
      },
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const token = localStorage.getItem("authToken");
          await axios.patch(`${API_CONST.UPDATE_JOB_STATUS}/${job.id}`, 
            { status: 'closed' },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setJobs(jobs.map((j) =>
            j.id === job.id ? { ...j, status: "Inactive" } : j
          ));
          message.success(`Job "${job.title}" has been closed successfully`);
        } catch (error) {
          console.error("Error closing job:", error);
          message.error("Failed to close job");
        }
      },
    });
  };

  const handleViewDetails = (jobId) => {
    navigate(`/customer/my-jobs/${jobId}`);
  };

  const handleJobTitleClick = (jobId) => {
    navigate(`/customer/my-jobs/${jobId}`);
  };

  const getActionMenuItems = (job) => {
    const items = [];
    
    if (hasPermission('job.view')) {
      items.push({
        key: "view",
        label: "View Details",
        icon: <EyeOutlined />,
        onClick: () => handleViewDetails(job.id),
      });
    }
    
    if (hasPermission('job.edit')) {
      items.push({
        key: "edit",
        label: "Edit Job",
        icon: <EditOutlined />,
        onClick: () => handleEditJob(job.id),
      });
    }

    if (job.status === "Active" && hasPermission('job.delete')) {
      items.push({
        key: "close",
        label: "Close Job",
        icon: <CloseCircleOutlined />,
        danger: true,
        onClick: () => handleCloseJob(job),
      });
    }

    return items;
  };

  return (
    <MyJobsWrapper>
      <PageHeader>
        <h1>My Jobs</h1>
        <p>View and manage your job postings</p>
      </PageHeader>
      <Row gutter={16} className="metrics-row">
        <Col xs={24} sm={8} md={8}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#e6f7ff" }}>
                <FileTextOutlined style={{ color: "#1890ff", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{activeCount}</h3>
                <p className="metric-label">Job Live</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8} md={8}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f0f5ff" }}>
                <UserOutlined style={{ color: "#597ef7", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{totalProfiles}</h3>
                <p className="metric-label">Profile Received</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8} md={8}>
          <Card className="metric-card" hoverable>
            <div className="metric-content">
              <div className="metric-icon" style={{ background: "#f6ffed" }}>
                <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 24 }} />
              </div>
              <div className="metric-info">
                <h3 className="metric-value">{totalHired}</h3>
                <p className="metric-label">Talent Hired</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="tabs-section">
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            className={activeTab === "Active" ? "tab-button active" : "tab-button"}
            onClick={() => setActiveTab("Active")}
          >
            Active ({activeCount})
          </Button>
          <Button
            className={activeTab === "Draft" ? "tab-button active" : "tab-button"}
            onClick={() => setActiveTab("Draft")}
          >
            Draft ({draftCount})
          </Button>
          <Button
            className={activeTab === "Pending Approval" ? "tab-button active" : "tab-button"}
            onClick={() => setActiveTab("Pending Approval")}
          >
            Pending Approval ({pendingCount})
          </Button>
          <Button
            className={activeTab === "Inactive" ? "tab-button active" : "tab-button"}
            onClick={() => setActiveTab("Inactive")}
          >
            Inactive ({inactiveCount})
          </Button>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <PermissionGate permission="job.create">
            <Button
              icon={<UploadOutlined />}
              onClick={() => setBulkUploadVisible(true)}
            >
              Bulk Upload
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              className="post-job-btn"
              onClick={() => navigate("/customer/post-job")}
            >
              Post Job
            </Button>
          </PermissionGate>
        </div>
      </div>

      <BulkUploadJobs
        visible={bulkUploadVisible}
        onClose={() => setBulkUploadVisible(false)}
        userRole="Customer"
        onUploadComplete={fetchJobs}
      />

      <Spin spinning={loading}>
      <div className="jobs-list">
        {filteredJobs.length === 0 && !loading ? (
          <Card className="empty-card">
            <div style={{ textAlign: "center", padding: "40px 0" }}>
              <FileTextOutlined style={{ fontSize: 48, color: "#d9d9d9", marginBottom: 16 }} />
              <p style={{ color: "#8c8c8c", margin: 0 }}>
                No {activeTab.toLowerCase()} jobs found
              </p>
            </div>
          </Card>
        ) : (
          filteredJobs.map((job) => (
            <Card key={job.id} className="job-card" hoverable>
              <div className="job-header">
                <div className="job-title-section">
                  <p className="job-id">Job id: {job.id}</p>
                  <h3
                    className="job-title"
                    onClick={() => handleJobTitleClick(job.id)}
                  >
                    {job.title}
                  </h3>
                  <p className="job-meta">
                    <Tag color="cyan">{job.type}</Tag>
                    <Tag color="blue">{job.location}</Tag>
                    {job.rawStatus === 'pending_approval' && <Tag color="gold">Pending Approval</Tag>}
                    {job.rawStatus === 'draft' && <Tag color="orange">Draft</Tag>}
                    <span className="experience">{job.experience}</span>
                  </p>
                </div>
                <div className="job-actions">
                  <span className="posted-date">Posted on {job.postedDate}</span>
                  <Dropdown
                    menu={{ items: getActionMenuItems(job) }}
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <MoreOutlined className="action-icon" />
                  </Dropdown>
                </div>
              </div>

              <div className="job-stats">
                <div className="stat-item" style={{ color: "#1890ff" }}>
                  <h4 style={{ color: "#1890ff" }}>{matchedCounts[job.id] !== undefined ? matchedCounts[job.id] : "—"}</h4>
                  <p>Matched</p>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <h4>{job.submittedProfiles}</h4>
                  <p>Submitted</p>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item">
                  <h4>{job.interviewing}</h4>
                  <p>Interviewing</p>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item rejected">
                  <h4>{job.rejected}</h4>
                  <p>Rejected</p>
                </div>
                <div className="stat-divider"></div>
                <div className="stat-item hired">
                  <h4>{job.hired}</h4>
                  <p>Hired</p>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>
      </Spin>
    </MyJobsWrapper>
  );
};

export default MyJobs;
